import { ImageFileData } from "./components/universal/image-cropper";
import { Accessor, JSXElement } from "solid-js";
import { IQuillEditorWrapper } from "./components/universal/quill-editor";
import { IImageCropperWrapper } from "./components/universal/image-cropper";

export type AppProps = {
    salonId: number;
}
export type Salon = {
    id: number;
    name: string;
};
export type ProgressProps = {
    step: number;
    onBeforeNavigatingToAnotherStep?: (target: Step) => Promise<boolean>;
};
export type Step1_RouteData = {
    Resources: any;
    ProfileImage: {
        ScholarshipEntrantProfileImageId: string;
        URL: string;
    };
    Model: any;
};
/**
 * https://stackoverflow.com/questions/50851263/how-do-i-require-a-keyof-to-be-for-a-property-of-a-specific-type
 */
export type KeyOfType<T, TFilter> = { [K in keyof T as (T[K] extends TFilter ? K : never)]: T[K] }
export type EntrantProfile = {
    id?: number;
    salonId: number;
    biography: string;
    biographyEditor: IQuillEditorWrapper;
    personal: {
        firstName: string;
        lastName: string;
    };
    profile: {
        salonEntrantImageId: string;
        imageUrl: string;
    };
    address: {
        streetAddress1: string;
        streetAddress2: string;
        state: string;
        city: string;
        countryId: string;
        postalCode: string;
    };
    contact: {
        email: string;
        phoneNumber: string;
    };
    socials: {
        facebook: string;
        instagram: string;
        twitter: string;
    };
    catalogue: {
        wantsFree: boolean;
        wantsAdditional: boolean;
        additionalOrdered: number;
    };
    isStudent: boolean;
    student: {
        age: number;
        school: {
            name: string;
            instructors: string;
            claimsIsTitle1: boolean;
            isTitle1: boolean;
        };
        parentOrLegalGuardian: {
            email: string;
            name: string;
            phoneNumber: string;
        }
    };
    acceptedTermsAndConditions: boolean;
}
export type Step = {
    index: number;
    name: string;
    label?: string;
    url?: string;
    disabled?: boolean;
};
export type SalonCategory = {
    id: number;
    name: string;
};
export type SalonSubmission = {
    id: number;
    category: SalonCategory;
};
export type NewImage = {
    Id: number;
    Name: string;
    Description: string;
    URL: string;
    Width: number;
    Height: number;
    FileSizeBytes: number;
};
export type NewImageDto = {
    Id: number;
    URL: string;
    Width: number;
    Height: number;
    FileSizeBytes: number;
};
export type SalonSubmissionImage = {
    id: number;
    newImage: NewImage;
    isDeleted: boolean;
    isPrimary: boolean;
};
export type LivingArtistArtwork = {
     Id: string;
     ArtistId: number;
     Title: string;
     Width: string;
     Height: string;
     Measurements: string;
     Depth: string;
     UnitOfMeasurement: string;
     Medium: string;
     Comment: string;
     DateAdded: string;
     FirstImage: ArtworkImage;
     SecondImage: ArtworkImage;
     Images: ArtworkImage[];
};
export type ArtworkImage = {
    Id: number;
    IsDeleted: boolean;
    IsPrimary: boolean;
    NewImage: NewImage;
}
export type Step2Data = {
    session: UserSession;
    facultyMembers: LivingArtistArtwork[];
    resources: {
        categories: any[];
    }
}
export type EditArtworkData = {
    session: UserSession;
    artwork: LivingArtistArtwork
}
export type EditArtworkImageData = {
    id: number;
    artwork: EditArtworkModel;
    newImage: NewImage;
    isPrimary: boolean;
    isDeleted: boolean;
}
export type NewImageBasic = {
    id?: number;
    url: string;
}
export type EditArtworkModel = {
    LivingArtistId: string;
    Id: string;
    Title: string;
    Medium: string;
    Location: string;
    Height: string;
    Width: string;
    Depth: string;
    UnitOfMeasurement: string;
    Description: string;
    FirstImage: ArtworkImage;
    SecondImage: ArtworkImage;
    FirstImageUrl: string;
    SecondImageUrl: string;
    FirstImageCropper: IImageCropperWrapper;
    SecondImageCropper: IImageCropperWrapper;
    DescriptionEditor: IQuillEditorWrapper;
};
export type ListItem = {
    Text: string; Value: string;
};
export enum ToastType {
    Error, Info
};
export type ToastData = {
    header: string;
    caption?: string;
    body?: string;
    html?: () => JSXElement;
    type?: ToastType;
}
export type MemberDto = {
    Id: number;
    Username: string;
    FirstName: string;
    LastName: string;
    Permissions: string[];
    MemberLevel: string;
};
export type UserSession = {
    atelierId?: number;
    isAdmin: boolean;
    isRegistered: boolean;
    cultureCode: string;
    canApply: boolean;
    isLoggedIn: boolean;
    itemCount: number;
    member: MemberDto;
};
export type ArtworkImageData = {
    Id: string;
    IsPrimary: boolean;
    IsDeleted: boolean;
    Artwork: {
        Id: string; Title: string;
    };
    NewImage: {
        Id: string; URL: string;
    };
    ImageUrl: string;
    ImageEditor: IImageCropperWrapper;
};
export type EditArtworkImage = {
    ScholarshipArtworkId: number;
    IsPrimary: boolean;
    NewImage: NewImage;
}
export type Member = {
    Id: number;
    FirstName: string;
    LastName: string;
    Email: string;
};
export type SalonEntrant = {
    id: number;
    cataloguesOrdered: number;
    firstName: string;
    lastName: string;
    email: string;
    totalFees: number|undefined;
    amountPaid: number|undefined;
    address: {
        streetLine1: string;
        streetLine2: string;
        city: string;
        state: string;
        postalCode: string;
        countryId: number;
    };
};
export type InvoiceItem = {
    itemCode: string;
    description: string;
    calue: number;
};
export type Step3Data = {
    canApply: boolean;
    salonEntrant: SalonEntrant;
    invoice: InvoiceItem[];
};
export type Address = {
    StreetLine1: string,
    StreetLine2: string,
    City: string,
    StateName: string,
    PostalCode: string,
    CountryId: string
}
export interface IAddress {
    StreetLine1: string,
    StreetLine2: string,
    City: string,
    StateName: string,
    PostalCode: string,
    CountryCode: string;
    Latitude: string;
    Longitude: string;
}
export type Ad = {
    Text: string;
    Image: NewImageDto;
    URL: string;
}
export type Atelier = {
    Id: string;
    Name: string;
    Subtitle: string;
    WebsiteURL: string;
    YearSchoolFounded: string;
    AccreditationDescription: string;
    CourseLanguagesArray: string[];
    AccreditationsArray: string[];
    Students: string;

    FullTimeInstructors: string;
    PartTimeInstructors: string;

    FullTimeTuition: string;
    FullTimeTuitionPeriodId: string;
    FullTimeTuitionCurrency: string;

    PartTimeTuition: string;
    PartTimeTuitionPeriodId: string;
    PartTimeTuitionCurrency: string;
    
    OffersAccommodation: boolean;
    ContactName: string;
    ContactEmail: string;
    PhoneNumber: string;
    StreetAddress1: string,
    StreetAddress2: string,
    City: string,
    StateName: string,
    CountryId: string
    PostalCode: string;
    Latitude: string;
    Longitude: string;

    Abstract: string;
    AbstractEditor: IQuillEditorWrapper;
    Summary: string;
    SummaryEditor: IQuillEditorWrapper;
    
    PhotoId: string;
    ProfileImage: NewImage;
    ProfileImageUrl: string;
    PhotoWrapper: IImageCropperWrapper;
};
export type ValidationFunction = (value: HTMLInputElement) => any;
export type CompressedSelectList = string[][];
export type FacultyMember = {
    Id: string;
    FirstName: string;
    LastName: string;
    Ordinal: string;
    Position: string;
    Biography: string;
    IsDeleted: boolean;
    CreatedDateUTC: string;
    LastModifiedDateUTC: string;
    PhotoId: string;
    Photo: NewImage;
}
export type NewImageModel = {
    Id: string;
    URL: string;
    ImageFile: Blob;
    ImportUrl: string;
}
export type FacultyMemberModel = {
    Id: string;
    FirstName: string;
    LastName: string;
    Position: string;
    Ordinal: number;
    AtelierId: string;
    ArtistId: string;
    PhotoId: string;
    Biography: string;
    BiographyEditor: IQuillEditorWrapper;
    Photo: NewImageModel;
    PhotoEditor: IImageCropperWrapper;
}
export type AtelierImage = {
    Id: string;
    AtelierId: string;
    ImageType: string;
    Priority: string;
    NewImageId: string;
    ImageRoleId: string;
    ImageRole: { Id: string; Name: string; Code: string },
    NewImage: NewImage;
    Description: string;
}
export type AtelierImageModel = AtelierImage & {
    Name: string;
    OriginalFilename: string;
    ImageFile: Blob;
    DescriptionEditor: IQuillEditorWrapper;
    NewImageEditor: IImageCropperWrapper;
}
export const ImageType = {
    "InstructorWork": "InstructorWork",
    "StudentWork": "StudentWork",
    "Photos": "Photos"
};

export type ImageFileDataListItem = {
    file: ImageFileData;
    order: number;
    id: string;
    entityId: string;
    isPrimary: boolean;
};